import { useState } from 'react'

import { DataTablePaginate } from '@/common/ui/table/data-table-paginate'
import { useTablePagination } from '@/common/hooks/useTablePaginate'
import useQueryGetPlayerTransactionsById from '../../../../api/hooks/useQueryGetPlayerTransactionsById'
import { transactionColumns } from './constants'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import DateRangeButtons from '@/common/components/DateRangeButtons'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import { Button } from '@/common/ui/button'
import { Download } from 'lucide-react'
import { useMutationExportPlayerCsv } from '@/modules/manage-player/api/hooks/useMutationExportPlayerCsv'
import { exportPlayerTransactionsCsv } from '@/modules/manage-player/api/services'
import { ExportCsvAmountModal } from '@/common/components/ExportCsvAmountModal'

interface TransactionTableProps {
	playerId: string
}

export default function TransactionTable({ playerId }: TransactionTableProps) {
	const { pageSize, currentPage } = useTablePagination()
	const { dateFilter } = useDateFilter()
	const exportCsvMutation = useMutationExportPlayerCsv()
	const [showExportModal, setShowExportModal] = useState(false)

	const transactionPayload = {
		playerId: playerId,
		skip: (currentPage - 1) * pageSize,
		take: pageSize,
		startDate: dateFilter.startDate || undefined,
		endDate: dateFilter.endDate || undefined,
	}

	const { data: transactionData, isLoading: isTransactionLoading } =
		useQueryGetPlayerTransactionsById(transactionPayload)

	const handleExportCsv = (amount: number) => {
		exportCsvMutation.mutate({
			filename: `player_transactions_${playerId}`,
			exportFn: () =>
				exportPlayerTransactionsCsv({
					playerId,
					startDate: dateFilter.startDate || undefined,
					endDate: dateFilter.endDate || undefined,
					take: amount || transactionData?.total || 1000,
					skip: 0,
					columnMappings: {
						createdAt: 'วันที่',
						type: 'ประเภท',
						amount: 'จำนวนเงิน',
						promotionName: 'โปรโมชั่น',
						bonusAmount: 'จำนวนโบนัส',
						processor: 'ประเภทการทำรายการ',
						status: 'สถานะ',
					},
				}),
		})
	}

	return (
		<div className="space-y-4">
			<div className="flex flex-col items-end gap-4">
				<Button
					variant="outline"
					onClick={() => setShowExportModal(true)}
					disabled={
						exportCsvMutation.isPending || !transactionData?.data?.length
					}
					className="flex items-center gap-2"
					size="sm"
				>
					<Download className="h-4 w-4" />
					โหลด CSV
				</Button>
				<DateRangePicker />
				<DateRangeButtons />
			</div>
			<DataTablePaginate
				columns={transactionColumns}
				data={transactionData?.data || []}
				totalItems={transactionData?.total || 0}
				isLoading={isTransactionLoading}
			/>
			<ExportCsvAmountModal
				open={showExportModal}
				onOpenChange={setShowExportModal}
				onConfirm={handleExportCsv}
				defaultAmount={transactionData?.total || 1000}
				maxAmount={100000}
				isLoading={exportCsvMutation.isPending}
			/>
		</div>
	)
}
